import { useEffect, useState } from 'react';
import { ouvreDocument, telechargeFichier } from '../../api/documents.js';
import { paieApi } from '../../api/resources';
import { BoutonDocument } from '../../components/BoutonDocument.jsx';
import { Shell } from '../../components/layout/Shell.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { DEVISE, fmt, fmtH } from '../../lib/currency';
import { dateSuisse } from '../../lib/dates';
import { C } from '../../theme/palette';

/**
 * Déclaration annuelle des salaires.
 *
 * <p>Tout vient du serveur : les lignes sont les récapitulatifs annuels que
 * portent les certificats de salaire, le barème est celui du décompte mensuel.
 * L'écran ne recalcule rien — il montre, contrôle et exporte. Les exports
 * (CSV pour le tableur, XML dans la structure Swissdec ELM 5, PDF) partent du
 * même calcul, et l'écran dit ce que le XML n'est pas : une transmission
 * certifiée.
 *
 * @param {object} props propriétés de l'écran
 * @param {object} props.soc société courante
 * @param {Function} props.onNav navigation principale
 * @param {Function} props.onClose retour à l'équipe
 * @param {boolean} [props.estMixte] entreprise à deux univers
 * @param {string} [props.univers] univers courant
 * @param {Function} [props.setUnivers] bascule d'univers
 * @returns {JSX.Element} l'écran
 */
export function DeclarationAnnuelle({ soc, onNav, onClose, estMixte, univers, setUnivers }) {
  const anneeCourante = new Date().getFullYear();
  const [annee, setAnnee] = useState(anneeCourante);
  const [declaration, setDeclaration] = useState(null);
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(true);
  const [export_, setExport] = useState({ enCours: '', erreur: '', fait: '' });

  useEffect(() => {
    let actif = true;
    setChargement(true);
    setErreur('');
    paieApi
      .declaration(annee)
      .then((d) => actif && setDeclaration(d))
      .catch((e) => actif && setErreur(e?.message || 'La déclaration n’a pas pu être établie.'))
      .finally(() => actif && setChargement(false));
    return () => {
      actif = false;
    };
  }, [annee]);

  const exporte = async (format) => {
    setExport({ enCours: format, erreur: '', fait: '' });
    try {
      const chemin = paieApi.cheminExport(annee, format);
      if (format === 'pdf') {
        await ouvreDocument(chemin);
        setExport({ enCours: '', erreur: '', fait: 'PDF ouvert dans un nouvel onglet.' });
      } else {
        const nom = await telechargeFichier(chemin);
        setExport({ enCours: '', erreur: '', fait: `Fichier ${nom} enregistré.` });
      }
    } catch (e) {
      setExport({ enCours: '', erreur: e?.message || 'L’export a échoué.', fait: '' });
    }
  };

  const montant = (v) => DEVISE + ' ' + fmt(v);
  const controles = declaration?.controles || [];
  const bloquants = controles.filter((c) => c.gravite === 'bloquant').length;

  return (
    <Shell
      active="equipe"
      onNav={onNav}
      soc={soc}
      back
      onBack={onClose}
      pageTitle="📊 Déclaration annuelle"
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      <Card style={{ marginBottom: 12 }}>
        <Bar
          icon="📊"
          title="Déclaration annuelle des salaires"
          color={C.navy}
          sub="Par employé et en total : salaire AVS, AC plafonné, LPP coordonné, impôt à la source, 13e"
          right={
            <select
              aria-label="Année"
              value={annee}
              onChange={(e) => setAnnee(+e.target.value)}
              style={{
                border: '1px solid ' + C.border,
                borderRadius: 7,
                padding: '5px 8px',
                fontSize: 11,
                fontWeight: 700,
                background: C.white,
              }}
            >
              {[0, 1, 2, 3, 4].map((o) => (
                <option key={anneeCourante - o} value={anneeCourante - o}>
                  {anneeCourante - o}
                </option>
              ))}
            </select>
          }
        />
        <div style={{ padding: 12 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0,1fr))',
              gap: 8,
            }}
          >
            <Btn
              label={export_.enCours === 'csv' ? '⏳ CSV…' : '⬇ CSV (tableur)'}
              color={C.green}
              full
              disabled={!declaration || !!export_.enCours}
              onClick={() => exporte('csv')}
            />
            <Btn
              label={export_.enCours === 'elm' ? '⏳ XML…' : '⬇ XML ELM 5'}
              color={C.teal}
              full
              disabled={!declaration || !!export_.enCours}
              onClick={() => exporte('elm')}
            />
            <Btn
              label={export_.enCours === 'pdf' ? '⏳ PDF…' : '📄 PDF récapitulatif'}
              color={C.purple}
              full
              disabled={!declaration || !!export_.enCours}
              onClick={() => exporte('pdf')}
            />
          </div>
          {export_.fait && (
            <div style={{ fontSize: 11, color: C.green, marginTop: 6 }}>{export_.fait}</div>
          )}
          {export_.erreur && (
            <div style={{ fontSize: 11, color: C.red, marginTop: 6 }}>{export_.erreur}</div>
          )}
          <div style={{ fontSize: 11, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
            Le XML suit la <b>structure Swissdec ELM 5 « SalaryDeclaration »</b> ; il n&apos;est{' '}
            <b>ni certifié ni transmis</b> — à valider avec votre caisse de compensation ou votre
            fiduciaire avant tout envoi. Les montants sont ceux des décomptes mensuels et des
            certificats de salaire.
          </div>
        </div>
      </Card>

      {chargement && (
        <Card style={{ marginBottom: 12 }}>
          <div style={{ padding: 14, fontSize: 11, color: C.muted }}>Calcul de l&apos;année…</div>
        </Card>
      )}
      {erreur && !chargement && (
        <Card style={{ marginBottom: 12 }}>
          <div style={{ padding: 14, fontSize: 11, color: C.red }}>{erreur}</div>
        </Card>
      )}

      {declaration && !chargement && (
        <>
          <Card style={{ marginBottom: 12 }}>
            <Bar
              icon={bloquants ? '⛔' : controles.length ? '⚠' : '✅'}
              title="Contrôles avant bouclement"
              color={bloquants ? C.red : controles.length ? C.amber : C.green}
              sub={
                controles.length
                  ? `${controles.length} point(s) à vérifier, dont ${bloquants} bloquant(s)`
                  : 'Numéros AVS, IBAN et décomptes mensuels au complet'
              }
            />
            {controles.length > 0 && (
              <div style={{ padding: '6px 12px 10px' }}>
                {controles.map((c, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      gap: 8,
                      padding: '6px 0',
                      borderBottom: '1px solid ' + C.border,
                      fontSize: 11,
                    }}
                  >
                    <span
                      style={{
                        flexShrink: 0,
                        fontWeight: 800,
                        color: c.gravite === 'bloquant' ? C.red : C.amber,
                      }}
                    >
                      {c.gravite === 'bloquant' ? 'Bloquant' : 'À vérifier'}
                    </span>
                    <span style={{ minWidth: 0 }}>
                      {c.employe && <b>{c.employe} — </b>}
                      {c.message}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card style={{ marginBottom: 12 }}>
            <Bar
              icon="∑"
              title={`Totaux ${declaration.annee}`}
              color={C.navy}
              sub={`${declaration.totaux.employes} employé(s) · ${fmtH(declaration.totaux.heures || 0)} pointées`}
            />
            <div
              style={{
                padding: 12,
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
                gap: 8,
              }}
            >
              {[
                ['Salaire brut', declaration.totaux.salaireBrut, C.navy],
                ['dont 13e salaire', declaration.totaux.treizieme, C.navy],
                ['Salaire AVS / AI / APG', declaration.totaux.salaireAvs, C.teal],
                ['Salaire AC (plafonné)', declaration.totaux.salaireAc, C.teal],
                ['Salaire coordonné LPP', declaration.totaux.salaireLppCoordonne, C.teal],
                ['Retenues employés', declaration.totaux.totalDeductions, C.red],
                ['Impôt à la source', declaration.totaux.impotSource, C.amber],
                ['Salaire net', declaration.totaux.salaireNet, C.green],
              ].map(([l, v, col]) => (
                <div
                  key={l}
                  style={{ background: col + '10', borderRadius: 8, padding: '8px 10px' }}
                >
                  <div style={{ fontSize: 11, color: col }}>{l}</div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: col }}>{montant(v)}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card style={{ marginBottom: 12 }}>
            <Bar
              icon="🏛"
              title={`Barème ${declaration.bareme.annee}`}
              color={C.grey}
              sub="Taux du décompte mensuel et plafonds légaux de l'année"
            />
            <div style={{ padding: 12, fontSize: 11, color: C.ink, lineHeight: 1.7 }}>
              <div>
                AVS / AI / APG <b>{declaration.bareme.tauxAvs} %</b> · AC{' '}
                <b>{declaration.bareme.tauxAc} %</b> · AANP <b>{declaration.bareme.tauxAanp} %</b> ·{' '}
                {/* Sans assurance IJM, le taux est nul et la mention n'a pas lieu d'être. */}
                {declaration.bareme.tauxIjm > 0 && (
                  <>
                    IJM <b>{declaration.bareme.tauxIjm} %</b> ·{' '}
                  </>
                )}
                LPP <b>{declaration.bareme.tauxLppDefaut} %</b> à défaut de taux propre
              </div>
              <div>
                Plafond AC : <b>{montant(declaration.bareme.plafondAcAnnuel)}</b> par an (
                {montant(declaration.bareme.plafondAcMensuel)} par mois)
              </div>
              <div>
                LPP : seuil d&apos;entrée <b>{montant(declaration.bareme.seuilEntreeLpp)}</b>,
                déduction de coordination{' '}
                <b>{montant(declaration.bareme.deductionCoordinationLpp)}</b>, salaire maximal{' '}
                <b>{montant(declaration.bareme.salaireMaxLpp)}</b>, coordonné minimal{' '}
                {montant(declaration.bareme.salaireCoordonneMinimum)}
              </div>
            </div>
          </Card>

          {declaration.employes.length === 0 && (
            <Card style={{ marginBottom: 12 }}>
              <div style={{ padding: 14, fontSize: 11, color: C.muted }}>
                Aucun employé n&apos;était en poste en {declaration.annee}.
              </div>
            </Card>
          )}
          {declaration.employes.map((e) => (
            <Card key={e.employeeId} style={{ marginBottom: 10 }}>
              <div style={{ padding: '11px 13px 4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 800, color: C.ink }}>
                      {e.nomComplet}
                    </div>
                    <div style={{ fontSize: 11, color: C.muted }}>
                      N° AVS {e.numeroAvs || '—'} · {e.moisAvecSalaire} mois avec salaire
                      {e.dateEntree ? ' · entrée ' + dateSuisse(e.dateEntree) : ''}
                      {e.dateSortie ? ' · sortie ' + dateSuisse(e.dateSortie) : ''}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 11, color: C.muted }}>Brut</div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: C.navy }}>
                      {montant(e.salaireBrut)}
                    </div>
                  </div>
                </div>
              </div>
              <div
                style={{
                  padding: '6px 13px 10px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0,1fr))',
                  gap: 6,
                }}
              >
                {[
                  ['13e', e.treizieme],
                  ['Indemnités', e.indemnites],
                  ['Heures', null, fmtH(e.heures || 0)],
                  ['Sal. AVS', e.salaireAvs],
                  ['Sal. AC', e.salaireAc],
                  ['LPP coord.', e.salaireLppCoordonne],
                  ['Cot. AVS', e.cotisationAvs],
                  ['Cot. AC', e.cotisationAc],
                  ['AANP', e.cotisationAanp],
                  ['Cot. LPP', e.cotisationLpp],
                  ['Impôt source', e.impotSource],
                  ['Net', e.salaireNet],
                ].map(([l, v, texte]) => (
                  <div
                    key={l}
                    style={{ background: C.navy + '08', borderRadius: 7, padding: '5px 7px' }}
                  >
                    <div style={{ fontSize: 11, color: C.muted }}>{l}</div>
                    <div style={{ fontSize: 11, fontWeight: 800, color: C.ink }}>
                      {texte ?? fmt(v)}
                    </div>
                  </div>
                ))}
              </div>
              {e.controles.length > 0 && (
                <div style={{ padding: '0 13px 8px', fontSize: 11, color: C.amber }}>
                  {e.controles.map((c, i) => (
                    <div key={i} style={{ color: c.gravite === 'bloquant' ? C.red : C.amber }}>
                      • {c.message}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ padding: '0 13px 12px' }}>
                <BoutonDocument
                  chemin={`/employes/${e.employeeId}/certificat-de-salaire/document`}
                  params={{ annee: declaration.annee }}
                  label={`📄 Certificat de salaire ${declaration.annee} (PDF)`}
                  color={C.purple}
                  full
                  sm
                />
              </div>
            </Card>
          ))}
        </>
      )}
    </Shell>
  );
}
